import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  FileCheck,
  Search,
  Clock,
  RefreshCw,
  Eye,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { Input } from '../../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  viewed: { label: 'Viewed', color: 'bg-emerald-100 text-emerald-700', icon: Eye },
  partially_fulfilled: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
  fulfilled: { label: 'Fulfilled', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-500', icon: AlertCircle },
};

const StaffDocumentRequestsPage = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      params.append('limit', '50');

      const response = await axios.get(`${API}/portal/staff/queue/document-requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch document requests:', err);
      setError(err.response?.data?.detail || 'Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchRequests();
  }, [token, statusFilter]);

  const filteredRequests = requests.filter(r => {
    if (!searchTerm) return true;
    const docType = r.document_type?.toLowerCase() || '';
    const requestId = r.request_id?.toLowerCase() || '';
    return docType.includes(searchTerm.toLowerCase()) || 
           requestId.includes(searchTerm.toLowerCase());
  });

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="staff-document-requests">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Document Requests</h1>
          <p className="text-slate-500 mt-1">Track pending document requests from clients</p>
        </div>
        <Button onClick={fetchRequests} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[200px] relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by document type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="viewed">Viewed</SelectItem>
            <SelectItem value="partially_fulfilled">Partial</SelectItem>
            <SelectItem value="fulfilled">Fulfilled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span>Showing {filteredRequests.length} of {total} requests</span>
      </div>

      {/* Requests Table */}
      {loading ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 rounded" />
            ))}
          </div>
        </div>
      ) : filteredRequests.length > 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Document Type</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Case</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Created</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Due</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRequests.map((request) => {
                const status = statusConfig[request.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                const isOverdue = request.due_date && new Date(request.due_date) < new Date();
                return (
                  <tr key={request.request_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-stone-900">{request.document_type || 'Document'}</p>
                      {request.description && (
                        <p className="text-xs text-slate-500 truncate max-w-xs">{request.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Link 
                        to={`/staff/cases/${request.case_id}`}
                        className="text-sm text-emerald-600 hover:text-emerald-700"
                      >
                        View Case
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                        <StatusIcon size={12} />
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${
                        request.priority === 'high' ? 'text-red-600' :
                        request.priority === 'medium' ? 'text-amber-600' : 'text-slate-600'
                      }`}>
                        {request.priority || 'Normal'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-500">
                        {request.created_at ? new Date(request.created_at).toLocaleDateString() : '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-slate-500'}`}>
                        {request.due_date ? new Date(request.due_date).toLocaleDateString() : '—'}
                        {isOverdue && ' (Overdue)'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link 
                        to={`/staff/cases/${request.case_id}/documents`}
                        className="text-emerald-600 hover:text-emerald-700 text-sm font-medium"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <FileCheck size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No pending requests</h3>
          <p className="text-slate-500">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'All document requests have been fulfilled'}
          </p>
        </div>
      )}
    </div>
  );
};

export default StaffDocumentRequestsPage;
